/**
 * CompanyStampPage — iter-218c (Feb 2026)
 *
 * Upload / preview / clear the company's single official stamp.
 * Used automatically on every approved certificate (and any future
 * document that requires the company seal).
 *
 * Route: /control-panel-settings/company-mgmt/employees/stamp
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Stamp, Upload, Loader2, Save, Trash2, ImagePlus } from 'lucide-react';

import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = process.env.REACT_APP_BACKEND_URL;
// iter-218d (Feb 2026) — Stamp limit raised to 5 MB per user request.
// The base64 data-URL is ~33% larger than the raw file, so 7 MB is the
// hard ceiling on the string length before we reject the upload.
const MAX_SIZE = 7 * 1024 * 1024;

export default function CompanyStampPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [stamp, setStamp]     = useState('');   // data URL
  const [meta, setMeta]       = useState({ updated_at: null, updated_by: null });
  const [dirty, setDirty]     = useState(false);

  const loadStamp = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/certificates/company-stamp`, {
        headers: getAuthHeaders(),
      });
      setStamp(r.data?.stamp_data_url || '');
      setMeta({ updated_at: r.data?.updated_at, updated_by: r.data?.updated_by });
      setDirty(false);
    } catch {
      toast.error(t('فشل تحميل الختم', 'Failed to load stamp'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadStamp(); /* eslint-disable-next-line */ }, []);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('اختر صورة فقط', 'Please pick an image file'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      if (dataUrl.length > MAX_SIZE) {
        toast.error(t(
          'الصورة كبيرة جداً — الحد الأقصى 5 ميجابايت.',
          'Image too large — max 5 MB.',
        ));
        return;
      }
      setStamp(dataUrl);
      setDirty(true);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!stamp) {
      toast.error(t('لا يوجد ختم للحفظ', 'No stamp to save'));
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/api/admin/certificates/company-stamp`,
        { stamp_data_url: stamp },
        { headers: getAuthHeaders() },
      );
      setMeta({ updated_at: r.data?.updated_at, updated_by: r.data?.updated_by });
      setDirty(false);
      toast.success(t('تم حفظ الختم', 'Stamp saved'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const clearStamp = async () => {
    if (!window.confirm(t('حذف الختم الحالي؟', 'Clear the current stamp?'))) return;
    try {
      await axios.post(
        `${API}/api/admin/certificates/company-stamp/clear`,
        {},
        { headers: getAuthHeaders() },
      );
      setStamp('');
      setMeta({ updated_at: null, updated_by: null });
      setDirty(false);
      toast.success(t('تم الحذف', 'Cleared'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Clear failed'));
    }
  };

  return (
    <DashboardLayout
      title="الختم الرسمي"
      titleEn="Company Stamp"
      subtitle="ختم الشركة الافتراضي المستخدم على الشهادات والمستندات الرسمية"
      subtitleEn="The default company stamp used on certificates and formal documents"
      screenCode="STAMP"
      backPath="/control-panel-settings/company-mgmt/employees"
      onBack={() => navigate('/control-panel-settings/company-mgmt/employees')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'التحكم بإدارة الشركة', labelEn: 'Company Management Control', path: '/control-panel-settings/company-mgmt' },
        { labelAr: 'إعدادات الموظفين', labelEn: 'Employees Settings', path: '/control-panel-settings/company-mgmt/employees' },
        { labelAr: 'الختم', labelEn: 'Stamp' },
      ]}
    >
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="company-stamp-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-white/[0.02] border border-white/10 p-6 sm:p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <Stamp className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {t('الختم الرسمي للشركة', 'Official Company Stamp')}
              </h2>
              <p className="text-xs text-white/50 mt-0.5">
                {t('يظهر تلقائياً على كل شهادة مُعتمدة', 'Auto-applied on every approved certificate')}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>
          ) : (
            <div className="space-y-5">
              <div
                className="min-h-[220px] rounded-xl border-2 border-dashed border-white/10 bg-white/[0.015] flex items-center justify-center p-6"
                data-testid="stamp-preview"
              >
                {stamp ? (
                  <img
                    src={stamp}
                    alt="Company stamp"
                    className="max-h-[220px] max-w-full object-contain rounded"
                    data-testid="stamp-preview-img"
                  />
                ) : (
                  <div className="text-center">
                    <ImagePlus className="w-10 h-10 mx-auto mb-2 text-white/25" />
                    <p className="text-sm text-white/40">
                      {t('لا يوجد ختم — ارفع صورة', 'No stamp yet — upload one')}
                    </p>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={onPickFile}
                data-testid="stamp-file-input"
              />

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.06]"
                  data-testid="stamp-upload-btn"
                >
                  <Upload className="w-4 h-4 me-1.5" />
                  {stamp ? t('اختيار صورة أخرى', 'Choose another') : t('اختيار الختم', 'Choose stamp')}
                </Button>

                {stamp && (
                  <Button
                    onClick={clearStamp}
                    variant="outline"
                    className="border-red-500/40 bg-red-500/[0.06] text-red-200 hover:bg-red-500/[0.12]"
                    data-testid="stamp-clear-btn"
                  >
                    <Trash2 className="w-4 h-4 me-1.5" />
                    {t('حذف', 'Clear')}
                  </Button>
                )}

                <Button
                  onClick={save}
                  disabled={saving || !dirty}
                  className="ms-auto bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
                  data-testid="stamp-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 me-1.5 animate-spin" /> : <Save className="w-4 h-4 me-1.5" />}
                  {t('حفظ', 'Save')}
                </Button>
              </div>

              {meta.updated_at && (
                <div className="text-[11px] text-white/40 pt-2 border-t border-white/5">
                  {t('آخر تحديث', 'Last updated')}: {new Date(meta.updated_at).toLocaleString(isAr ? 'ar-AE' : 'en-GB')}
                  {meta.updated_by ? ` • ${meta.updated_by}` : ''}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
